import type { IceServer } from "./types.ts";

export type IceFailureReason = "turn-disabled" | "embedded-turn-secret" | "missing-turn-credentials" | "invalid-ice-server";

export type IceResolution = { ok: true; iceServers: IceServer[] } | { ok: false; reason: IceFailureReason };

export type PeerConnectionConfig = {
  iceServers: IceServer[];
  bundlePolicy: "max-bundle";
  rtcpMuxPolicy: "require";
};

function isTurnUrl(url: string): boolean {
  return /^turns?:/i.test(url);
}

function turnUrlHasUserInfo(url: string): boolean {
  const match = /^(?:turns?):([^?]*)/i.exec(url);
  return match !== null && match[1].includes("@");
}

function copyServer(server: IceServer): IceServer {
  const copy: IceServer = { urls: [...server.urls] };
  if (server.username !== undefined) copy.username = server.username;
  if (server.credential !== undefined) copy.credential = server.credential;
  return copy;
}

export function resolveIceServers(input: { turnEnabled: boolean; iceServers: readonly IceServer[] }): IceResolution {
  if (!input.turnEnabled) {
    if (input.iceServers.length > 0) return { ok: false, reason: "turn-disabled" };
    return { ok: true, iceServers: [] };
  }

  const iceServers: IceServer[] = [];
  for (const server of input.iceServers) {
    const urls = server.urls;
    const urlsAreStrings = Array.isArray(urls) && urls.length > 0 && urls.every((url) => typeof url === "string" && url.length > 0);
    if (!urlsAreStrings) return { ok: false, reason: "invalid-ice-server" };
    for (const url of urls) {
      if (turnUrlHasUserInfo(url)) return { ok: false, reason: "embedded-turn-secret" };
      const missingCredential = server.username === undefined || server.username.length === 0 || server.credential === undefined || server.credential.length === 0;
      if (isTurnUrl(url) && missingCredential) return { ok: false, reason: "missing-turn-credentials" };
    }
    iceServers.push(copyServer(server));
  }
  return { ok: true, iceServers };
}

export function peerConnectionConfig(resolved: { iceServers: IceServer[] }): PeerConnectionConfig {
  return {
    iceServers: resolved.iceServers,
    bundlePolicy: "max-bundle",
    rtcpMuxPolicy: "require",
  };
}
